'use client';

import { pushSearchResultEvent } from '@/globals/analytics/events/pushSearchEvent';
import { getSearchState } from '@/globals/dataCollector/searchDataCollector/functions/getSearchState';
import { getSearchURL } from '@/globals/dataCollector/searchDataCollector/functions/getSearchURL';
import { searchIcon } from '@/uiIcons/searchIcon.css';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { occupy } from '@/uiPrimitives/layout/occupy';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { icon } from '@/uiPrimitives/media/icon';
import { textBlock } from '@/uiPrimitives/typography/text';
import { hiddenAccessible } from '@/uiPrimitives/utility/display.css';
import { useId } from 'react';
import { useSearchHistory } from './useSearchHistory';
import { usePageHistory } from '@/globals/pageWrappers/usePageHistory';
import { useDOPRouter } from '@/globals/routing/DOPRouter';
import { addTestSelector } from '@dop/shared/helpers/testSelector';
import { translateInline } from '@/globals/translate/translateInline';

export const IndependentSearchForm = ({
	className,
	defaultValue,
}: {
	defaultValue?: string;
	className?: string;
}) => {
	const id = useId();
	const router = useDOPRouter();
	const searchHistory = useSearchHistory();
	const [pageHistory] = usePageHistory();

	return (
		<form
			action={(formData) => {
				const term = formData.get('term');

				if (typeof term !== 'string') return;
				const searchParams = new URLSearchParams();
				const searchURL = getSearchURL({ term, page: 1, facetSlug: 'all' });
				const searchState = getSearchState(searchParams);

				router.push(searchURL);
				pushSearchResultEvent({
					searchState: searchState,
					searchHistory: searchHistory,
					pageHistory,
				});
			}}
			className={classes(className, zIndex({ isolation: 'isolate' }))}
		>
			<label htmlFor={id} className={classes(hiddenAccessible)}>
				{translateInline({
					en: 'Search in Business.gov.nl',
					nl: 'Zoeken in Ondernemersplein',
				})}
			</label>
			<div
				className={classes(
					shelf({ gridTemplateColumns: 'minmax(0, 1fr) auto' }),
				)}
			>
				<input
					type="text"
					placeholder={translateInline({
						en: 'Search in Business.gov.nl',
						nl: 'Zoeken in Ondernemersplein',
					})}
					name="term"
					id={id}
					defaultValue={defaultValue}
					autoCapitalize="none"
					autoComplete="off"
					className={classes(
						textBlock({ color: 'positive:noHover' }),
						box({
							paddingBlock: '3 | h1',
							paddingInline: indentSize,
							backgroundColor: 'negative',
							borderWidth: '1px',
							borderStyle: 'solid',
							borderColor: 'positive',
						}),
						zIndex({
							'zIndex:focusVisible': '1',
						}),
					)}
					{...addTestSelector('uiIndependentSearchForm')}
				/>
				<button
					type="submit"
					aria-label={translateInline({
						en: 'Search',
						nl: 'Zoeken',
					})}
					className={classes(
						box({
							inlineSize: 'fit-content',
							padding: '3 | h1',
							backgroundColor: 'positive',
						}),
					)}
					{...addTestSelector('uiIndependentSearchFormButton')}
				>
					<span
						className={classes(
							occupy({ inlineSize: 'capSize', blockSize: 'capSize' }),
						)}
					>
						<i
							className={classes(
								icon({ icon: searchIcon, blockSize: '3 | h1' }),
							)}
						/>
					</span>
				</button>
			</div>
		</form>
	);
};
